import type * as Sentry from "@sentry/react";

export type SentryTransactionEvent = Parameters<NonNullable<Sentry.BrowserOptions["beforeSendTransaction"]>>[0];

const INVITE_CODE_PATH = /\/register\/[^/?#\s]+/g;
const OAUTH_CALLBACK_PATH = "/oauth-redirect";
const OAUTH_CALLBACK_QUERY_VALUES = /([?&](?:code|state)=)[^&#\s]*/g;
const CHANGE_PASSWORD_ID_VALUE = /(changePasswordId=)[^&#\s]*/g;
const TOKEN_QUERY_VALUE = /([?&]token=)[^&#\s]*/g;
const SENSITIVE_KEYS = new Set(["changePasswordId", "password"]);
const REDACTED = "[REDACTED]";
const MAX_DEPTH = 10;

const redactInviteCode = (text: string): string => text.replace(INVITE_CODE_PATH, "/register/:inviteCode");

const redactOAuthCallbackQuery = (text: string): string =>
	text.includes(OAUTH_CALLBACK_PATH) ? text.replace(OAUTH_CALLBACK_QUERY_VALUES, `$1${REDACTED}`) : text;

const redactChangePasswordId = (text: string): string => text.replace(CHANGE_PASSWORD_ID_VALUE, `$1${REDACTED}`);

const redactTokenQuery = (text: string): string => text.replace(TOKEN_QUERY_VALUE, `$1${REDACTED}`);

export const redactText = (text: string): string =>
	redactTokenQuery(redactChangePasswordId(redactOAuthCallbackQuery(redactInviteCode(text))));

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
	if (value === null || typeof value !== "object") return false;
	const prototype = Object.getPrototypeOf(value);
	return prototype === Object.prototype || prototype === null;
};

const redactInPlace = (value: unknown, depth: number): void => {
	if (depth >= MAX_DEPTH) return;
	if (Array.isArray(value)) {
		value.forEach((item, index) => {
			if (typeof item === "string") value[index] = redactText(item);
			else redactInPlace(item, depth + 1);
		});
		return;
	}
	if (!isPlainObject(value)) return;
	for (const [key, item] of Object.entries(value)) {
		if (SENSITIVE_KEYS.has(key)) value[key] = REDACTED;
		else if (typeof item === "string") value[key] = redactText(item);
		else redactInPlace(item, depth + 1);
	}
};

export const redactSentryErrorEvent = (event: Sentry.ErrorEvent): Sentry.ErrorEvent => {
	redactInPlace(event, 0);
	return event;
};

export const redactReplayRecordingEvent = <T extends object>(event: T): T => {
	redactInPlace(event, 0);
	return event;
};

export const redactSentryTransaction = (event: SentryTransactionEvent): SentryTransactionEvent => {
	redactInPlace(event, 0);
	return event;
};

const redactSentryBreadcrumb = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb => {
	redactInPlace(breadcrumb, 0);
	return breadcrumb;
};

export default redactSentryBreadcrumb;
